import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import ImageActionOverlay from './ImageActionOverlay';
import ImageTile from './ImageTile';

const TemplateImageTile = ({
  imageUrl,
  fallbackImageUrl,
  title,
  description,
  shotCount,
  className,
  interactive,
  showHoverActions,
  showPrimaryAction,
  primaryLabel,
  badgeLabel,
  badgeIconClassName,
  metaLabel,
  matchLabel,
  extraMenuActions,
  flipOverlayAtViewportEdge,
  detachOverlayFromColumn,
  onDetails,
  onOpen,
  onReprompt,
  onStyleReference,
}) => {
  const handleOpen = (event) => {
    if (!onOpen) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onOpen();
  };

  const menuActions = useMemo(() => {
    const actions = [];

    if (onReprompt) {
      actions.push({
        key: 'reprompt',
        label: 'Reprompt',
        iconClassName: 'fa-regular fa-pen-to-square',
        onClick: onReprompt,
      });
    }

    if (onStyleReference) {
      actions.push({
        key: 'style-reference',
        label: 'Style Reference',
        iconClassName: 'fa-regular fa-clone',
        onClick: onStyleReference,
      });
    }

    if (Array.isArray(extraMenuActions)) {
      actions.push(...extraMenuActions);
    }

    return actions;
  }, [extraMenuActions, onReprompt, onStyleReference]);

  return (
    <ImageTile
      imageUrl={imageUrl}
      fallbackImageUrl={fallbackImageUrl}
      title={title}
      description={description}
      shotCount={shotCount}
      className={className}
      interactive={interactive}
      badgeLabel={badgeLabel}
      badgeIconClassName={badgeIconClassName}
      metaLabel={metaLabel}
      matchLabel={matchLabel}
      flipOverlayAtViewportEdge={flipOverlayAtViewportEdge}
      detachOverlayFromColumn={detachOverlayFromColumn}
      onDetails={onDetails}
      overlay={showHoverActions ? (
        <ImageActionOverlay
          showPrimaryAction={showPrimaryAction}
          primaryLabel={primaryLabel}
          menuActions={menuActions}
          onPrimaryAction={handleOpen}
        />
      ) : null}
    />
  );
};

TemplateImageTile.propTypes = {
  imageUrl: PropTypes.string,
  fallbackImageUrl: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  shotCount: PropTypes.number,
  className: PropTypes.string,
  interactive: PropTypes.bool,
  showHoverActions: PropTypes.bool,
  showPrimaryAction: PropTypes.bool,
  primaryLabel: PropTypes.string,
  badgeLabel: PropTypes.string,
  badgeIconClassName: PropTypes.string,
  metaLabel: PropTypes.string,
  matchLabel: PropTypes.string,
  extraMenuActions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string.isRequired,
    iconClassName: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
  })),
  flipOverlayAtViewportEdge: PropTypes.bool,
  detachOverlayFromColumn: PropTypes.bool,
  onDetails: PropTypes.func,
  onOpen: PropTypes.func,
  onReprompt: PropTypes.func,
  onStyleReference: PropTypes.func,
};

TemplateImageTile.defaultProps = {
  imageUrl: null,
  title: '',
  description: '',
  shotCount: 0,
  className: '',
  interactive: false,
  showHoverActions: true,
  showPrimaryAction: true,
  primaryLabel: 'Recreate',
  badgeLabel: '',
  badgeIconClassName: '',
  metaLabel: '',
  matchLabel: '',
  extraMenuActions: [],
  flipOverlayAtViewportEdge: false,
  detachOverlayFromColumn: false,
  onDetails: undefined,
  onOpen: undefined,
  onReprompt: undefined,
  onStyleReference: undefined,
};

export default TemplateImageTile;
